/* PLAT-04: accessibility preferences applied app-wide through <html> data attributes.
   The shell already honors `data-motion="reduce"` (styles/base.css); `global.css` in this folder adds
   `data-text` (text scale via --cx-text-scale), `data-contrast="high"`, `data-captions="on"` and
   `data-sr="on"`. The last saved preferences are kept in localStorage so they apply before the API
   answers on the next load (convenience only; the API is the source of truth). */
import type { AccessibilityPrefs } from './types';

export const A11Y_KEY = 'cx.settings.a11y';

export const DEFAULT_A11Y: AccessibilityPrefs = {
  screenReader: false, textScale: 1, reduceMotion: false, highContrast: false, captions: false, voiceControl: false, altText: true, keyboardHints: true,
};

/** "Larger text" switch value. */
export const LARGE_TEXT_SCALE = 1.25;

export function applyAccessibility(p: Partial<AccessibilityPrefs>, root: HTMLElement = document.documentElement): void {
  const v = { ...DEFAULT_A11Y, ...p };
  if (v.reduceMotion) root.dataset.motion = 'reduce'; else delete root.dataset.motion;
  const scale = Math.min(2, Math.max(0.8, Number(v.textScale) || 1));
  if (scale !== 1) {
    root.dataset.text = scale > 1 ? 'large' : 'small';
    root.style.setProperty('--cx-text-scale', String(scale));
  } else {
    delete root.dataset.text;
    root.style.removeProperty('--cx-text-scale');
  }
  if (v.highContrast) root.dataset.contrast = 'high'; else delete root.dataset.contrast;
  if (v.captions) root.dataset.captions = 'on'; else delete root.dataset.captions;
  if (v.screenReader) root.dataset.sr = 'on'; else delete root.dataset.sr;
}

export function saveAccessibility(p: AccessibilityPrefs): void {
  try { localStorage.setItem(A11Y_KEY, JSON.stringify(p)); } catch { /* storage blocked */ }
}

export function loadAccessibility(): AccessibilityPrefs | null {
  try {
    const raw = localStorage.getItem(A11Y_KEY);
    return raw ? { ...DEFAULT_A11Y, ...(JSON.parse(raw) as Partial<AccessibilityPrefs>) } : null;
  } catch {
    return null;
  }
}

/** For other features (voice, media players): whether captions should be shown by default. */
export function captionsPreferred(root: HTMLElement = document.documentElement): boolean {
  return root.dataset.captions === 'on';
}
